import { useEffect, useRef } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuthStore } from '@/features/auth/auth.store';
import { Logo } from '@/components/Logo';

/**
 * Recebe o código efêmero do callback do Azure SSO e troca pelos tokens reais.
 *
 * O fluxo é:
 * 1. API redireciona aqui com `?code=<uuid>`
 * 2. Chamamos `POST /api/v1/auth/azure/exchange`
 * 3. Gravamos a sessão e redirecionamos para o app
 *
 * O código expira em 30 s — se a troca falhar, voltamos para `/entrar?erro=azure`.
 */
export function AzureCallbackPage() {
  const search = useSearch({ strict: false }) as { code?: string; destino?: string };
  const navigate = useNavigate();
  const { loginWithAzureResult } = useAuthStore();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const code = search.code;
    if (!code) {
      void navigate({ to: '/entrar', search: { erro: 'azure' } });
      return;
    }

    fetch('/api/v1/auth/azure/exchange', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Troca falhou');
        return res.json() as Promise<{
          accessToken: string;
          refreshToken: string;
          expiresIn: number;
          mfaRequired: boolean;
          user: { id: string; name: string; email: string };
        }>;
      })
      .then((result) => {
        loginWithAzureResult(result);
        void navigate({ href: search.destino ?? '/' });
      })
      .catch(() => {
        void navigate({ to: '/entrar', search: { erro: 'azure' } });
      });
  }, []);

  return (
    <div className="grid min-h-full place-items-center bg-background">
      <div className="flex flex-col items-center gap-6 text-center">
        <Logo />
        <div className="flex gap-1.5" aria-label="Carregando">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="h-2 w-2 rounded-full bg-primary animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">Concluindo autenticação…</p>
      </div>
    </div>
  );
}
